import { Injectable } from '@angular/core';
import { MatDialog } from '@angular/material/dialog';
import { Money } from '@principle-theorem/accounting';
import {
  IMohSendClaimDialogData,
  MohSendClaimDialogComponent,
  MohService,
} from '@principle-theorem/ng-funding-requests';
import { AccountingFunctionsService } from '@principle-theorem/ng-principle-shared';
import { DialogPresets } from '@principle-theorem/ng-shared';
import {
  getBaseTransaction,
  Invoice,
  MohClaimFundingRequest,
  MohPreApprovalFundingRequest,
  Practice,
  resolveClaimItems,
  ServiceProviderHandler,
  stafferToNamedDoc,
  toClaimableItems,
  Transaction,
} from '@principle-theorem/principle-core';
import {
  FundingRequestStatus,
  IClaimableItem,
  IHealthcareClaim,
  IInvoice,
  IMohClaimFundingRequest,
  IMohExtendedData,
  IMohPreApprovalFundingRequest,
  IPatient,
  IPractice,
  ITransaction,
  ServiceCodeType,
  TransactionAction,
  TransactionProvider,
  TransactionStatus,
} from '@principle-theorem/principle-core/interfaces';
import {
  DocumentReference,
  Firestore,
  Region,
  snapshot,
  toNamedDocument,
  uid,
  WithRef,
} from '@principle-theorem/shared';
import { sum } from 'lodash';
import { Observable, of } from 'rxjs';
import { map } from 'rxjs/operators';
import {
  ITransactionProvider,
  TransactionProviderType,
} from '../transaction-provider';

@Injectable()
export class MohTransactionProvider implements ITransactionProvider {
  providerId = TransactionProvider.MOH;
  providerType = TransactionProviderType.MOH;
  providerRegions = [Region.NewZealand];
  isEnabled$ = of(true);

  constructor(
    private _accountFunctions: AccountingFunctionsService,
    private _dialog: MatDialog,
    private _mohService: MohService
  ) {}

  canCapture$(invoice: WithRef<IInvoice>): Observable<boolean> {
    return this._hasPendingMohTransaction$(invoice).pipe(
      map(
        (hasPendingMohTransaction) =>
          !hasPendingMohTransaction &&
          !Invoice.isPaid(invoice) &&
          Invoice.canAddTransactions(invoice)
      )
    );
  }

  async capture(
    invoice: WithRef<IInvoice>,
    claim: IHealthcareClaim
  ): Promise<DocumentReference<ITransaction> | undefined> {
    const practice = await Firestore.getDoc(invoice.practice.ref);
    const hasValidSettings =
      await this._mohService.validatePracticeMohSettings(practice);
    if (!hasValidSettings) {
      return;
    }

    const claimItems = toClaimableItems(
      resolveClaimItems(invoice, claim)
    ).filter((item) => {
      const serviceCode = ServiceProviderHandler.findServiceCode(
        item.serviceCode.code
      );
      return serviceCode?.type === ServiceCodeType.MOH;
    });

    const data = await this._buildSendClaimDialogData(
      invoice,
      practice,
      claimItems,
      claim
    );

    const formsCompleted = await this._dialog
      .open<
        MohSendClaimDialogComponent,
        IMohSendClaimDialogData,
        DocumentReference<IMohClaimFundingRequest>
      >(MohSendClaimDialogComponent, DialogPresets.medium({ data }))
      .afterClosed()
      .toPromise();
    if (!formsCompleted) {
      await Firestore.deleteDoc(data.claimRequestRef);
      return;
    }

    const transactionRef = await this._addTransaction(
      invoice,
      claimItems,
      claim
    );
    if (!transactionRef) {
      return;
    }

    const mohClaim = await Firestore.getDoc(data.claimRequestRef);
    await Firestore.patchDoc(mohClaim.ref, {
      data: {
        ...mohClaim.data,
        transactionRef,
      },
    });
    if (mohClaim.data.approvalRequestRef) {
      await Firestore.patchDoc(mohClaim.data.approvalRequestRef, {
        status: FundingRequestStatus.Redeemed,
      });
    }

    return transactionRef;
  }

  private async _addTransaction(
    invoice: WithRef<IInvoice>,
    claimItems: IClaimableItem[],
    claim: IHealthcareClaim
  ): Promise<DocumentReference<ITransaction<IMohExtendedData>> | undefined> {
    const baseTransaction = await getBaseTransaction(invoice);
    const amount = Money.amount(
      sum(claimItems.map((item) => item.serviceCode.amount * item.quantity))
    );
    const extendedData: IMohExtendedData = {
      amountClaimed: amount,
      items: claimItems,
    };
    const transaction = Transaction.init({
      ...baseTransaction,
      ...Transaction.internalReference(TransactionProvider.MOH),
      status: TransactionStatus.Pending,
      amount,
      attributedTo: claim.practitioner.ref,
      practiceRef: invoice.practice.ref,
      extendedData,
    });

    return this._accountFunctions.addTransactionToInvoice(
      invoice,
      transaction,
      TransactionAction.Add,
      claim
    );
  }

  private async _buildSendClaimDialogData(
    invoice: WithRef<IInvoice>,
    practice: WithRef<IPractice>,
    claimItems: IClaimableItem[],
    claim: IHealthcareClaim
  ): Promise<IMohSendClaimDialogData> {
    const patient = await Firestore.getDoc(Invoice.patientRef(invoice));
    const practitioner = await Firestore.getDoc(claim.practitioner.ref);
    const claimUid = uid();
    const claimRequestRef = await Firestore.addDoc<IMohClaimFundingRequest>(
      Practice.fundingRequestCol({ ref: invoice.practice.ref }),
      MohClaimFundingRequest.init({
        patient: toNamedDocument(patient),
        practitioner: stafferToNamedDoc(practitioner),
        data: {
          claimUid,
          invoiceRef: invoice.ref,
        },
      })
    );
    const approvalRequests = await this._getAvailableApprovalRequests(
      invoice.practice.ref,
      patient.ref
    );
    return {
      claimRequestRef,
      claim,
      claimItems,
      claimUid,
      patient,
      practitioner,
      invoice,
      practice,
      approvalRequests,
    };
  }

  private async _getAvailableApprovalRequests(
    practiceRef: DocumentReference<IPractice>,
    patientRef: DocumentReference<IPatient>
  ): Promise<WithRef<IMohPreApprovalFundingRequest>[]> {
    const patientRequests = await snapshot(
      MohPreApprovalFundingRequest.byPatient$(
        { ref: practiceRef },
        { ref: patientRef }
      )
    );

    return patientRequests.filter(
      (request) =>
        request.status === FundingRequestStatus.Approved &&
        !request.data.mohClaimRef
    );
  }

  private _hasPendingMohTransaction$(
    invoice: WithRef<IInvoice>
  ): Observable<boolean> {
    return Invoice.transactions$(invoice).pipe(
      map((transactions) =>
        transactions.some((transaction) =>
          this._isPendingMohTransaction(transaction)
        )
      )
    );
  }

  private _isPendingMohTransaction(
    transaction: WithRef<ITransaction>
  ): boolean {
    return (
      transaction.status === TransactionStatus.Pending &&
      transaction.provider === TransactionProvider.MOH
    );
  }
}
